import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  DateRangeKind,
  GlobalDateRange,
  GlobalTimeSettingsService,
  SmoothingValue,
} from 'src/app/services/global-time-settings/global-time-settings.service';
import * as moment from 'moment';

interface SelectOption<T> {
  viewValue: string;
  value: T;
}

@Component({
    selector: 'app-global-time-settings',
    templateUrl: './global-time-settings.component.html',
    styleUrls: ['./global-time-settings.component.scss'],
    standalone: false
})
export class GlobalTimeSettingsComponent implements OnInit {
  private appliedDateRange: GlobalDateRange;
  private appliedSmoothing: SmoothingValue;

  public isCollapsed = false;
  public showCustomDateRange = false;

  public settingsForm: UntypedFormGroup;
  public dateRangeControl = new UntypedFormControl();
  public smoothingControl = new UntypedFormControl();

  public dateRangeOptions: SelectOption<DateRangeKind>[] = [];
  public smoothingOptions: SelectOption<SmoothingValue>[] = [];

  public get currentDateRangeLabel(): string {
    const value = this.dateRangeControl?.value;
    return this.dateRangeOptions.find((opt) => opt.value === value)?.viewValue ?? '';
  }

  public get currentSmoothingLabel(): string {
    const value = this.smoothingControl?.value;
    return this.smoothingOptions.find((opt) => opt.value === value)?.viewValue ?? '';
  }

  public get hasPendingChanges(): boolean {
    const selectedDateRange = this.getSelectedDateRange();
    const selectedSmoothing = this.smoothingControl.value as SmoothingValue;

    if (!selectedDateRange || !selectedSmoothing) {
      return false;
    }

    return (
      !this.dateRangesAreEqual(selectedDateRange, this.appliedDateRange) ||
      selectedSmoothing !== this.appliedSmoothing
    );
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private globalTimeSettingsService: GlobalTimeSettingsService,
    private translateService: TranslateService
  ) {
    this.initOptions();
  }

  ngOnInit(): void {
    this.settingsForm = this.formBuilder.group({
      dateRange: this.dateRangeControl,
      smoothing: this.smoothingControl,
      filterDateStart: [new Date()],
      filterDateEnd: [new Date()],
    });

    // Load current values from service
    const currentDateRange = this.globalTimeSettingsService.getCurrentDateRange();
    const currentSmoothing = this.globalTimeSettingsService.getCurrentSmoothing();

    this.appliedDateRange = { ...currentDateRange };
    this.appliedSmoothing = currentSmoothing;

    this.dateRangeControl.setValue(currentDateRange.kind);
    this.smoothingControl.setValue(currentSmoothing);

    if (currentDateRange.kind === 'custom') {
      this.showCustomDateRange = true;
      if (currentDateRange.customStart) {
        this.settingsForm.controls['filterDateStart'].setValue(
          new Date(currentDateRange.customStart)
        );
      }
      if (currentDateRange.customEnd) {
        this.settingsForm.controls['filterDateEnd'].setValue(
          new Date(currentDateRange.customEnd)
        );
      }
    }
  }

  private initOptions(): void {
    this.dateRangeOptions = [
      {
        viewValue:
          this.translateService.instant('app.timePeriodLast24Hours') ||
          'letzte 24 Stunden',
        value: '24h',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodLastWeek') ||
          'letzte Woche',
        value: '7d',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodLast2Weeks') ||
          'letzte 2 Wochen',
        value: '14d',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodLastMonth') ||
          'letztes Monat',
        value: '1m',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodLastYear') ||
          'letztes Jahr',
        value: '1y',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodFromZeroMeasurement') ||
          'Von Nullmessung',
        value: 'zero',
      },
      {
        viewValue:
          this.translateService.instant('app.timePeriodSelectRange') ||
          'Zeitbereich auswählen',
        value: 'custom',
      },
    ];

    this.smoothingOptions = [
      {
        viewValue:
          this.translateService.instant('app.smoothingNone') || 'Keine Glättung',
        value: '0',
      },
      {
        viewValue:
          this.translateService.instant('app.smoothing12Hours') || '12 Stunden',
        value: '12h',
      },
      {
        viewValue: this.translateService.instant('app.smoothing1Day') || '1 Tag',
        value: '1d',
      },
      {
        viewValue:
          this.translateService.instant('app.smoothing1Week') || '1 Woche',
        value: '7d',
      },
      {
        viewValue:
          this.translateService.instant('app.smoothing2Weeks') || '2 Wochen',
        value: '14d',
      },
      {
        viewValue:
          this.translateService.instant('app.smoothing1Month') || '1 Monat',
        value: '30d',
      },
    ];
  }

  public onDateRangeChange(event: any): void {
    const kind: DateRangeKind = event.value;
    this.showCustomDateRange = kind === 'custom';
  }

  public applySettings(): void {
    const dateRange = this.getSelectedDateRange();
    const smoothing = this.smoothingControl.value as SmoothingValue;

    if (!dateRange || !smoothing || !this.hasPendingChanges) {
      return;
    }

    this.globalTimeSettingsService.setDateRange(dateRange);
    this.globalTimeSettingsService.setSmoothing(smoothing);
    this.appliedDateRange = { ...dateRange };
    this.appliedSmoothing = smoothing;
    this.settingsForm.markAsPristine();
  }

  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  private getSelectedDateRange(): GlobalDateRange | null {
    const kind = this.dateRangeControl.value as DateRangeKind;

    if (!kind) {
      return null;
    }

    if (kind !== 'custom') {
      return { kind };
    }

    const startValue = this.settingsForm?.controls['filterDateStart'].value;
    const endValue = this.settingsForm?.controls['filterDateEnd'].value;

    if (!startValue || !endValue) {
      return null;
    }

    return {
      kind: 'custom',
      customStart: moment(startValue).startOf('day').toISOString(),
      customEnd: moment(endValue).startOf('day').toISOString(),
    };
  }

  private dateRangesAreEqual(
    first: GlobalDateRange,
    second: GlobalDateRange
  ): boolean {
    return (
      first?.kind === second?.kind &&
      first?.customStart === second?.customStart &&
      first?.customEnd === second?.customEnd
    );
  }
}
